/**
 * WallboardStandalone.jsx
 * Die Raumanzeige unter der eigenen Adresse /wallboard — mit eigener
 * Anmeldung statt Microsoft-Konto.
 *
 * Bewusst getrennt von App.jsx: Diese Seite laeuft auf einem Rechner, an dem
 * niemand sitzt. Sie kennt weder Kopfleiste noch Benutzermenue und prueft die
 * Microsoft-Anmeldung gar nicht erst — sie fragt einfach die Wallboard-Daten
 * an und zeigt das Anmeldeformular, wenn der Server 401 antwortet.
 *
 * Ist ein Admin im selben Browser am Dashboard angemeldet, laesst der Server
 * ihn ohnehin durch; dann erscheint das Formular nie.
 */
import React, { useState, useEffect, useCallback } from 'react';
import WallboardPage from './WallboardPage';
import './WallboardPage.css';

const API_BASE = process.env.REACT_APP_API_URL !== undefined
  ? process.env.REACT_APP_API_URL : 'http://localhost:3001';

function Anmeldung({ onErfolg }) {
  const [benutzer, setBenutzer] = useState('');
  const [passwort, setPasswort] = useState('');
  const [fehler,   setFehler]   = useState(null);
  const [laeuft,   setLaeuft]   = useState(false);
  const [info,     setInfo]     = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/wallboard/status`, { credentials: 'include' })
      .then(r => r.json()).then(setInfo).catch(() => {});
  }, []);

  const absenden = async (e) => {
    e.preventDefault();
    setLaeuft(true); setFehler(null);
    try {
      const r = await fetch(`${API_BASE}/api/wallboard/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ benutzer, passwort }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setFehler(d.error || 'Anmeldung fehlgeschlagen'); return; }
      onErfolg();
    } catch {
      setFehler('Keine Verbindung zum Server');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="wallboard wallboard--login">
      <form className="wb-login" onSubmit={absenden}>
        <h1 className="wb-login-titel">Support-Wallboard</h1>
        {info && !info.eingerichtet ? (
          <p className="wb-login-hinweis">
            Es ist noch kein Zugang eingerichtet. Ein Administrator muss im
            Dashboard unter „Wallboard-Zugang" ein Passwort setzen.
          </p>
        ) : (
          <>
            <label className="wb-login-label">
              Benutzername
              <input value={benutzer} onChange={e => setBenutzer(e.target.value)}
                autoComplete="username" autoFocus />
            </label>
            <label className="wb-login-label">
              Passwort
              <input type="password" value={passwort} onChange={e => setPasswort(e.target.value)}
                autoComplete="current-password" />
            </label>
            {fehler && <div className="wb-login-fehler">{fehler}</div>}
            <button type="submit" disabled={laeuft || !benutzer || !passwort}>
              {laeuft ? 'Anmelden…' : 'Anmelden'}
            </button>
            {info?.sessionTage && (
              <p className="wb-login-hinweis">
                Die Anmeldung gilt {info.sessionTage} Tage. Dieser Bildschirm muss
                danach einmal neu angemeldet werden.
              </p>
            )}
          </>
        )}
      </form>
    </div>
  );
}

export default function WallboardStandalone() {
  // null = noch unbekannt, true = angemeldet, false = Formular zeigen
  const [angemeldet, setAngemeldet] = useState(null);

  // Vollbild-Modus: Aussenabstand von <body> weg und dunkler Hintergrund bis
  // an den Bildschirmrand. Nur hier, nicht im Reiter — dort wuerde es das
  // uebrige Dashboard verschieben. Gesetzt fuer die ganze Lebensdauer der
  // Seite, also auch waehrend der Anmeldung.
  useEffect(() => {
    document.documentElement.classList.add('wallboard-vollbild');
    document.body.classList.add('wallboard-vollbild');
    return () => {
      document.documentElement.classList.remove('wallboard-vollbild');
      document.body.classList.remove('wallboard-vollbild');
    };
  }, []);

  const pruefe = useCallback(() => {
    fetch(`${API_BASE}/api/wallboard/status`, { credentials: 'include' })
      .then(() => fetch(`${API_BASE}/api/wallboard?top=1`, { credentials: 'include' }))
      .then(r => setAngemeldet(r.ok))
      .catch(() => setAngemeldet(false));
  }, []);

  useEffect(() => { pruefe(); }, [pruefe]);

  if (angemeldet === null) {
    return <div className="wallboard"><div className="wallboard-kopf">
      <span className="wallboard-titel">Support-Wallboard</span>
    </div></div>;
  }
  if (!angemeldet) return <Anmeldung onErfolg={() => setAngemeldet(true)} />;
  return <WallboardPage />;
}
